<template>
  <div>
    <b-alert variant="danger" :show="workerData === undefined">
      <h4 class="alert-heading">Error fetching worker data</h4>
      <div class="alert-body">
        No worker found with this worker id. Check
        <b-link class="alert-link" :to="{ name: 'workers-list' }">
          Worker List
        </b-link>
        for other workers.
      </div>
    </b-alert>
    <div v-if="workerData && Object.keys(workerData).length > 0">
      <modal-salary-record
        :salary-record-data="salaryRecordData"
        :payment-method-status-options="paymentMethodStatusOptions"
        :user_id="workerData.id"
        @refetch-data="refetchData"
      />

      <modal-document
        apiUrl="/user_documents"
        :document-data="documentData"
        :user-document-type-options="userDocumentTypeOptions"
        :user_id="workerData.id"
        @refetch-data="refetchData"
      />

      <Modal-Pdf :title="title" :pdf-url="pdfUrl" />

      <modal-worker-expense-documents
        :worker-id="workerData.id"
        :worker-name="workerData.name_cn || workerData.name"
        modal-id="modal-worker-expense-docs-view"
      />

      <modal-image-viewer
        :image-list="previewImage"
        :selected_file="selected_file"
      ></modal-image-viewer>

      <b-card no-body class="p-1 px-2">
        <!-- Worker Info: Top col -->
        <div
          class="d-flex flex-wrap align-items-center justify-content-between"
        >
          <h4 class="mb-1 mb-sm-0 ml-0 font-weight-bolder text-nowrap">
            <font-awesome-icon
              class="mr-50"
              icon="fa-solid fa-person-digging"
            />
            師傅資料
          </h4>
          <div class="d-flex flex-wrap justify-content-end">
            <b-button
              v-if="ability.can('update', 'workers')"
              class="px-3 mx-1 mobile-w100 mb-1 mb-sm-0 backgroundColor1 no-border"
              :to="{ name: 'worker-edit', params: { id: workerData.id } }"
              ><feather-icon
                class="mr-50"
                size="14"
                icon="Edit2Icon"
              />編輯</b-button
            >
            <b-button
              class="px-3 mx-1 mobile-w100 mb-1 mb-sm-0 backgroundColor1 no-border"
              @click="$bvModal.show('modal-worker-expense-docs-view')"
            >
              <feather-icon class="mr-50" size="14" icon="DownloadIcon" />下載資料
            </b-button>
            <b-button
              class="mobile-w100 mb-1 mb-sm-0 px-3 mx-1"
              type="button"
              variant="outline-secondary"
              @click="hide"
              v-if="ability.can('read', 'workers')"
            >
              返回
            </b-button>
            <b-button
              class="px-3 mx-1 mobile-w100 mb-1 mb-sm-0 no-border backgroundColor3 colorBlack"
              variant="outline-danger"
              v-if="ability.can('delete', 'workers')"
              @click="deleteWorker(workerData.id)"
              ><feather-icon
                class="mr-50"
                size="14"
                icon="Trash2Icon"
              />刪除</b-button
            >
          </div>
        </div>
        <hr />
        <b-row class="info-wrap px-1">
          <b-col class="mb-1 mb-sm-0" cols="12" sm="12" md="3">
            <h4 class="mb-0 font-weight-bolder">{{ workerData.name }}</h4>
            <h5 class="mb-0 font-weight-bolder">{{ workerData.name_cn }}</h5>
            <br />
            <div class="d-flex">
              <div class="">工人註冊証號碼:</div>
              <div>{{ workerData.registration_certificate_number }}</div>
            </div>
            <div class="d-flex">
              <div class="">建造業安全訓練證明書號碼:</div>
              <div>{{ workerData.safety_training_certificate_number }}</div>
            </div>
            <br />
            <div class="">
              <label class="info-label">師傅編號:</label
              ><span>{{ workerData.user_number }}</span>
            </div>
            <!-- <div><label>職位:</label><span>{{ workerData.position }}</span></div> -->
            <div class="">
              <label class="info-label">狀態:</label>
              <span class="colorRed">
                <feather-icon
                  v-if="workerData.is_active"
                  icon="CheckIcon"
                  size="18"
                  class="mr-50 text-success"
                />
              </span>
              <span class="colorRed">
                <feather-icon
                  v-if="workerData.locked"
                  icon="LockIcon"
                  size="18"
                  class="mr-50 text-danger"
                />
              </span>
            </div>
            <div class="">
              <label class="info-label">加入日期:</label
              ><span>{{ workerData.join_date }}</span>
            </div>
          </b-col>

          <!-- gender -->
          <b-col title="gender" cols="12" sm="6" md="3">
            <div class="info-item">
              <span class="icon">
                <font-awesome-icon icon="fa-solid fa-person" class="" />
              </span>
              {{ workerData.gender && workerData.gender.name }}
            </div>
            <div class="info-item">
              <span class="icon">
                <font-awesome-icon icon="fa-solid fa-id-card" class="" />
              </span>
              {{ workerData.id_number }}
            </div>
            <div class="info-item">
              <span class="icon">
                <font-awesome-icon icon="fa-solid fa-calendar" class="" />
              </span>
              {{ workerData.date_of_birth }}
            </div>
          </b-col>

          <!-- tel -->
          <b-col cols="12" sm="6" md="3">
            <div class="info-item">
              <span class="icon">
                <font-awesome-icon icon="fa-solid fa-phone" class="" />
              </span>
              {{ workerData.phone }}
            </div>

            <div class="info-item">
              <span class="icon">
                <font-awesome-icon icon="fa-solid fa-envelope" class="" />
              </span>
              {{ workerData.email }}
            </div>
            <div class="info-item">
              <span class="icon">
                <font-awesome-icon icon="fa-solid fa-location-dot" class="" />
              </span>
              {{ workerData.address }}
            </div>
          </b-col>

          <!-- bank -->
          <b-col cols="12" sm="6" md="3">
            <div class="info-item">
              <span class="icon">
                <font-awesome-icon
                  icon="fa-solid fa-building-columns"
                  class=""
                />
              </span>
              {{ workerData.bank && workerData.bank.name }}
            </div>
            <div class="info-item">
              <span class="icon">
                <font-awesome-icon icon="fa-solid fa-credit-card" class="" />
              </span>
              {{ workerData.bank_account }}
            </div>
          </b-col>
        </b-row>
      </b-card>

      <b-row class="align-items-stretch mb-2">
        <b-col md="6" v-if="ability.can('projects', 'worker_detail')">
          <b-card class="h-100">
            <div class="d-flex align-items-center justify-content-between">
              <h4
                class="mb-0 ml-0 d-flex aling-items-center left-bold-border before-color-1 font-weight-bolder"
              >
                負責項目
              </h4>
            </div>
            <div class="mt-2">
              <b-table
                ref="refProjectListTable"
                class="position-relative"
                :items="fetchProjects"
                responsive
                :fields="projectTableColumns"
                primary-key="id"
                show-empty
                empty-text="未找到匹配的記錄"
              >
                <!-- Column: period -->
                <template #cell(period)="data">
                  {{
                    `${data.item.expect_start_date} - ${data.item.expect_end_date}`
                  }}
                </template>

                <!-- Column: project_status_id -->
                <template #cell(project_status_id)="data">
                  {{ getProjectStatusName(data.item.project_status_id) }}
                </template>

                <!-- Column: manager_id -->
                <template #cell(manager_id)="data">
                  {{ getManagerName(data.item.manager_id) }}
                </template>

                <!-- Column: is_completed -->
                <template #cell(is_completed)="data">
                  <feather-icon
                    v-if="data.item.is_completed"
                    icon="CheckIcon"
                    size="18"
                    class="mr-50 text-success"
                  />
                </template>

                <!-- Column: created_at -->
                <template #cell(created_at)="data">
                  <div class="d-flex">
                    {{ getDateTime(data.item.created_at) }}
                  </div>
                </template>

                <template #cell(actions)="data">
                  <b-dropdown
                    :class="`action-trigger-btn ${
                      projectsList.length <= 3 ? 'hor' : 'ver'
                    }`"
                    :right="$store.state.appConfig.isRTL"
                  >
                    <template #button-content>
                      <feather-icon
                        icon="EyeIcon"
                        size="16"
                        class="align-middle text-body"
                      />
                    </template>

                    <b-dropdown-item
                      v-if="ability.can('view_detail', 'projects')"
                      :to="{
                        name: 'order-view',
                        params: { id: data.item.id },
                      }"
                    >
                      <feather-icon icon="FileTextIcon" />
                      <span class="align-middle ml-50">訂單詳情</span>
                    </b-dropdown-item>
                    <!-- <b-dropdown-item
                      v-if="ability.can('update', 'user_claims')"
                      :to="{
                        name: 'worker-claim-create',
                        query: {
                          user_id: workerData.id,
                          project_id: data.item.id,
                        },
                      }"
                    >
                      <feather-icon icon="FileTextIcon" />
                      <span class="align-middle ml-50">報銷單據</span>
                    </b-dropdown-item> -->

                    <!-- <b-dropdown-item
                      v-if="ability.can('delete', 'workers')"
                      @click="removeItem(data.item.id)"
                    >
                      <feather-icon icon="Trash2Icon" />
                      <span class="align-middle ml-50">刪除資料</span>
                    </b-dropdown-item> -->
                  </b-dropdown>
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>
        <b-col md="6" v-if="ability.can('document', 'worker_detail')">
          <b-card id="downloadWrapper" style="height: 100%">
            <div class="d-flex align-items-center justify-content-between">
              <h4
                class="mb-0 ml-0 d-flex aling-items-center left-bold-border before-color-2 font-weight-bolder"
              >
                所有文件
              </h4>
              <!--  -->
              <b-button
                variant="primary"
                class="d-flex py-50 px-3 align-items-center"
                v-if="ability.can('document_upload', 'worker_detail')"
                @click="handleModelDocument(false)"
              >
                <feather-icon icon="PlusIcon" size="20" />
                上傳文件
              </b-button>
            </div>
            <div class="mt-2">
              <b-table
                ref="refWorkerDocumentsTable"
                class="position-relative"
                :items="documents"
                responsive
                :fields="documentTableColumns"
                primary-key="id"
                show-empty
                empty-text="未找到匹配的記錄"
              >
                <!-- :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc" -->
                <!-- Column: payment_method_id -->
                <template #cell(image_url)="data">
                  <!-- <a target="_blank" :href="data.item.file_url">下載</a> -->
                  <a
                    class="color1"
                    @click="previewImageSet(documents, data.index)"
                    >預覽</a
                  >
                </template>

                <template #cell(created_at)="data">
                  {{ getDateTime(data.item.created_at) }}
                </template>

                <template #cell(actions)="data">
                  <b-dropdown
                    v-if="
                      ability.can('document_update', 'worker_detail') ||
                      ability.can('document_delete', 'worker_detail')
                    "
                    class="action-trigger-btn"
                    :right="$store.state.appConfig.isRTL"
                  >
                    <template #button-content>
                      <feather-icon
                        icon="EyeIcon"
                        size="16"
                        class="align-middle text-body"
                      />
                    </template>

                    <b-dropdown-item
                      v-if="ability.can('document_update', 'worker_detail')"
                      @click="handleModelDocument(data.item)"
                    >
                      <feather-icon icon="EditIcon" />
                      <span class="align-middle ml-50">修改文件</span>
                    </b-dropdown-item>

                    <b-dropdown-item
                      v-if="ability.can('document_delete', 'worker_detail')"
                      @click="removeDocument(data.item.id)"
                    >
                      <feather-icon icon="Trash2Icon" />
                      <span class="align-middle ml-50">刪除文件</span>
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="align-items-stretch">
        <b-col md="6" v-if="ability.can('salaries', 'worker_detail')">
          <b-card class="h-100">
            <div class="d-flex align-items-center justify-content-between">
              <h4
                class="mb-0 ml-0 d-flex aling-items-center left-bold-border before-color-1 font-weight-bolder"
              >
                最近出糧記錄
              </h4>
              <div class="d-flex flex-wrap justify-content-end">
                <b-button
                  class="mobile-w100 mr-2"
                  :to="{
                    name: 'expenditure-salary-create',
                    query: { user_id: workerData.id },
                  }"
                  v-if="ability.can('salaries_create', 'worker_detail')"
                >
                  <feather-icon
                    class="mr-1"
                    icon="plusIcon"
                    size="18"
                  ></feather-icon
                  >新增</b-button
                >
                <!-- <b-button
                  class="mobile-w100"
                  :to="{
                    name: 'worker-salary-preview',
                    params: {
                      id: workerData.id,
                      start_date: getFormatedDate(
                        new Date(new Date().getFullYear(), 0, 1)
                      ),
                      end_date: getFormatedDate(new Date()),
                    },
                  }"
                  v-if="ability.can('salaries_print', 'worker_detail')"
                >
                  <feather-icon icon="FileTextIcon" />
                  列印記錄
                </b-button> -->
              </div>
            </div>
            <div class="mt-2">
              <b-table
                ref="refWorkerSalaryRecordsTable"
                class="position-relative"
                :items="salaryRecords"
                responsive
                :fields="tableColumns"
                primary-key="id"
                show-empty
                empty-text="未找到匹配的記錄"
              >
                <template #cell(prepayment)="data">
                  <div
                    v-if="
                      data.item.prepayment_total_amount &&
                      data.item.prepayment_total_amount > 0
                    "
                  >
                    <div class="text-primary font-weight-bold">
                      HK${{ formatCurrency(data.item.prepayment_total_amount) }}
                    </div>
                    <!-- 遍歷預支分配記錄 -->
                    <div
                      v-if="
                        data.item.prepayment_allocations &&
                        data.item.prepayment_allocations.length > 0
                      "
                    >
                      <div
                        v-for="(allocation, index) in data.item
                          .prepayment_allocations"
                        :key="`allocation-${allocation.id}`"
                      >
                        <b-link
                          :key="allocation.id"
                          :to="{
                            name: 'advance-payment-edit',
                            params: { id: allocation.prepayment.id },
                          }"
                          class="small text-muted"
                        >
                          {{ allocation.prepayment.prepayment_number }}
                        </b-link>
                        <span
                          v-if="
                            index < data.item.prepayment_allocations.length - 1
                          "
                          :key="`separator-${allocation.id}`"
                          class="small text-muted mx-1"
                        >
                        </span>
                      </div>
                    </div>
                    <!-- 向後兼容：如果沒有 prepayment_allocations 但有舊的 prepayment 結構 -->
                    <b-link
                      v-else-if="
                        data.item.prepayment &&
                        data.item.prepayment.prepayment_number
                      "
                      :to="{
                        name: 'advance-payment-edit',
                        params: { id: data.item.prepayment.id },
                      }"
                      class="small text-muted"
                    >
                      {{ data.item.prepayment.prepayment_number }}
                    </b-link>
                  </div>
                  <span v-else class="text-muted">--</span>
                </template>
                <template #cell(expense)="data">
                  <div v-if="data.item.expense && data.item.expense.length > 0">
                    <div
                      v-for="(expense, index) in data.item.expense"
                      :key="`expense-${expense.id}`"
                    >
                      <b-link
                        :key="`expense-${expense.id}`"
                        :to="{
                          name: 'expenditure-edit',
                          params: { id: expense.id },
                        }"
                      >
                        {{ expense.expense_number }}</b-link
                      >
                    </div>
                  </div>
                </template>

                <template #cell(salary_type)="data">
                  {{
                    data.item.salary_type &&
                    getSalaryType(data.item.salary_type).name
                  }}
                </template>
                <template #cell(gross_salary)="data">
                  <span>HK${{ formatCurrency(data.item.gross_salary) }}</span>
                </template>
                <template #cell(actual_salary)="data">
                  <span>HK${{ formatCurrency(data.item.actual_salary) }}</span>
                </template>

                <template #cell(year_month)="data">
                  <div
                    v-if="data.item.reference_year && data.item.reference_month"
                  >
                    {{
                      data.item.reference_year
                        ? data.item.reference_year + "年 "
                        : ""
                    }}
                    {{
                      data.item.reference_month
                        ? data.item.reference_month + "月"
                        : ""
                    }}
                  </div>
                  <div
                    v-else-if="
                      data.item.expense &&
                      data.item.expense.salary_year &&
                      data.item.expense.salary_month
                    "
                  >
                    {{
                      data.item.expense.salary_year
                        ? data.item.expense.salary_year + "年 "
                        : ""
                    }}
                    {{
                      data.item.expense.salary_month
                        ? data.item.expense.salary_month + "月"
                        : ""
                    }}
                  </div>
                  <div v-else-if="data.item.year_month">
                    {{ data.item.year_month }}
                  </div>
                </template>

                <template #cell(salary_receive_method_id)="data">
                 <div v-if="data.item.salary_receive_method_id">
                    {{
                      data.item.salary_receive_method_id &&
                      getSalaryReceiveMethod(data.item.salary_receive_method_id)
                        .name
                    }}
                    <span v-if="data.item.check_number">
                      - {{ data.item.check_number }}
                    </span>
                 </div> 
                </template>

                <template #cell(is_approved)="data">
                  <feather-icon
                    v-if="data.item.is_approved"
                    icon="CheckIcon"
                    size="18"
                    class="mr-50 text-success"
                  />
                </template>
                <template #cell(is_rejected)="data">
                  <feather-icon
                    v-if="data.item.is_rejected"
                    icon="CheckIcon"
                    size="18"
                    class="mr-50 text-success"
                  />
                </template>
                <template #cell(show_details)="row">
                  <b-button
                    size="sm"
                    @click="toggleRowDetails('project', row)"
                    class="mr-2"
                  >
                    {{ row.detailsShowing ? "隱藏" : "顯示" }}
                  </b-button>
                </template>

                <template #row-details="row">
                  <div>
                    <!-- <div class="info-container" v-if="row.item.invoice">
                <b-row class="p-2 backgroundColor3 mb-1">
                  <b-col cols="12">
                    <div class="row mb-50">
                      <b class="label">發票編號：</b>
                      <span>{{ row.item.invoice.invoice_number }}</span>
                    </div>

                    <div class="row mb-50">
                      <b class="label">地址：</b>
                      <span>{{ row.item.invoice.address }}</span>
                    </div>
                  </b-col>
                </b-row>
              </div> -->
                    <div class="info-container">
                      <div class="px-0">
                        <label
                          >PS. 應發工資 = 各項工資 + 各項佣金/花紅 +
                          各項其他津貼 + 各項逾時工作金額</label
                        >
                      </div>
                      <div class="px-0">
                        <label
                          >PS. 實發工資 = 各項工資 + 各項佣金/花紅 +
                          各項其他津貼 + 各項逾時工作金額 - 各項強積金(僱員) -
                          預支金額</label
                        >
                      </div>
                      <b-row
                        class="p-2 backgroundColor5 mb-1"
                        v-for="(worker, index) in row.item.salary_records"
                        :key="`cost${index}`"
                      >
                        <b-col cols="12" md="3">
                          <div class="row mb-50" v-if="worker.expense">
                            <b class="label">編號：</b>
                            <span>{{ worker.expense.expense_number }}</span>
                          </div>
                          <div class="row mb-50">
                            <b
                              class="label"
                              v-if="workerData.user_type_id === 1"
                              >員工：</b
                            >
                            <b
                              class="label"
                              v-else-if="workerData.user_type_id === 4"
                              >師傅：</b
                            >
                            <span>
                              {{
                                workerData.position
                                  ? `[${workerData.position}]`
                                  : ""
                              }}
                              {{ workerData.name_cn }}</span
                            >
                          </div>
                          <div
                            class="row mb-50"
                            v-if="row.item.salary_type === 1"
                          >
                            <b class="label">工資：</b>
                            <span
                              >{{ worker.working_days }}天 * ${{
                                worker.day_salary
                              }}每日 = ${{ worker.total_salary }}</span
                            >
                          </div>
                          <!-- <div class="row mb-50" v-if="worker.user && worker.user.payment_method_id == 2">
                      <b class="label">出糧月份：</b>
                     
                    <span>{{ worker.salary_year }}年 {{ worker.salary_month }}月</span>
                      
                    </div> -->
                          <div
                            class="row mb-50"
                            v-if="row.item.salary_type !== 1"
                          >
                            <b class="label" v-if="row.item.salary_type === 2"
                              >月薪：</b
                            >
                            <b class="label" v-if="row.item.salary_type === 3"
                              >工程薪金：</b
                            >
                            <span>${{ worker.total_salary || 0.0 }}</span>
                          </div>
                          <div class="row mb-50">
                            <b class="label">逾時工作：</b>
                            <span
                              >{{ worker.ot_hours || 0.0 }}小時 = 金額${{
                                worker.ot_amount || 0.0
                              }}</span
                            >
                          </div>
                          <div class="row mb-50">
                            <b class="label">佣金/花紅：</b>
                            <span>${{ worker.commission || 0.0 }}</span>
                          </div>
                          <div class="row mb-50">
                            <b class="label">其他津貼：</b>
                            <span
                              >${{ worker.allowance || 0.0 }}
                              {{
                                worker.allowance_remark
                                  ? ` - ${worker.allowance_remark}`
                                  : ""
                              }}</span
                            >
                          </div>
                          <div
                            class="row mb-50"
                            v-if="worker.mpf_employer || worker.mpf_employee"
                          >
                            <b class="label">強積金：</b>
                            <span
                              ><b>強積金(僱主)：</b>${{
                                worker.mpf_employer || 0.0
                              }}
                              <b>強積金(僱員)：</b>${{
                                worker.mpf_employee || 0.0
                              }}</span
                            >
                          </div>
                        </b-col>
                        <b-col cols="12" md="3">
                          <div class="row mb-50" v-if="worker.expense">
                            <b class="label">出糧日期：</b>
                            <span>{{ worker.expense.expense_date }}</span>
                          </div>
                          <div class="row mb-50" v-if="worker.invoice">
                            <b class="label">發票：</b>
                            <span>{{
                              `[${worker.invoice.invoice_number}] ${worker.invoice.name_cn} ${worker.invoice.address}`
                            }}</span>
                          </div>
                          <div class="row mb-50">
                            <b class="label">備註：</b>
                            <pre>{{ worker.remark || "--" }}</pre>
                          </div>
                        </b-col>
                      </b-row>

                      <!-- 日薪匯總卡片 -->
                      <div
                        v-if="
                          row.item.salary_type === 1 &&
                          getDailySalarySummary(row.item.salary_records)
                        "
                        class="mb-1"
                      >
                        <b-card
                          class="salary-summary-card"
                          style="background-color: #f0ffc2; margin-left: -1rem"
                        >
                          <h6 class="mb-1 font-weight-bold">工資匯總</h6>

                          <b-row>
                            <b-col cols="12" md="3">
                              <div class="summary-item mb-2">
                                <span class="summary-label"
                                  >總逾時工作金額：</span
                                >
                                <span class="summary-value font-weight-bold">
                                  {{
                                    formatCurrency(
                                      getDailySalarySummary(
                                        row.item.salary_records
                                      ).totalOtAmount
                                    )
                                  }}
                                </span>
                              </div>
                              <div class="summary-item mb-2">
                                <span class="summary-label">總佣金/花紅：</span>
                                <span class="summary-value font-weight-bold">
                                  {{
                                    formatCurrency(
                                      getDailySalarySummary(
                                        row.item.salary_records
                                      ).totalCommission
                                    )
                                  }}
                                </span>
                              </div>
                              <div class="summary-item mb-2">
                                <span class="summary-label">總其他津貼：</span>
                                <span class="summary-value font-weight-bold">
                                  {{
                                    formatCurrency(
                                      getDailySalarySummary(
                                        row.item.salary_records
                                      ).totalAllowance
                                    )
                                  }}
                                </span>
                              </div>
                            </b-col>
                            <!-- <b-col cols="12" md="3">
                      <div class="summary-item mb-2">
                        <span class="summary-label">總僱主強積金：</span>
                        <span class="summary-value font-weight-bold">
                          {{ formatCurrency(getDailySalarySummary(row.item.salary_records).totalMpfEmployer) }}
                        </span>
                      </div>
                      <div class="summary-item mb-2">
                        <span class="summary-label">總僱員強積金：</span>
                        <span class="summary-value font-weight-bold">
                          {{ formatCurrency(getDailySalarySummary(row.item.salary_records).totalMpfEmployee) }}
                        </span>
                      </div>
                    </b-col> -->
                          </b-row>
                          <hr />
                          <div class="summary-item">
                            <span class="summary-label">總工資金額：</span>
                            <span
                              class="summary-value font-weight-bold text-primary"
                            >
                              {{
                                formatCurrency(
                                  getDailySalarySummary(row.item.salary_records)
                                    .totalSalary
                                )
                              }}
                            </span>
                          </div>
                        </b-card>
                      </div>
                    </div>
                  </div>
                </template>
                <template #cell(export)="data">
                  <b-button v-if="ability.can('salaries_print', 'worker_detail')" variant="primary" @click="exportSalary(data.item)"
                    >列印</b-button
                  >
                  <!-- <span>列印修改進行中~</span> -->
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>
        <b-col md="6" v-if="ability.can('claims_document', 'worker_detail')">
          <b-card id="downloadWrapper" style="height: 100%">
            <div class="d-flex align-items-center justify-content-between">
              <h4
                class="mb-0 ml-0 d-flex aling-items-center left-bold-border before-color-2 font-weight-bolder"
              >
                報銷單據
              </h4>
              <b-button
                variant="primary"
                class="d-flex py-50 px-3 align-items-center"
                :to="{
                  name: 'worker-claim-create',
                  query: { user_id: workerData.id },
                }"
                v-if="ability.can('claims_document_create', 'worker_detail')"
              >
                <feather-icon icon="PlusIcon" size="20" />
                報銷資料</b-button
              >
            </div>
            <div class="mt-2">
              <b-table
                ref="refWorkerClaimsTable"
                class="position-relative"
                :items="claims"
                responsive
                :fields="documentTableColumns"
                primary-key="id"
                show-empty
                empty-text="未找到匹配的記錄"
              >
                <!-- :sort-by.sync="sortBy"
                :sort-desc.sync="isSortDirDesc" -->

                <template #cell(name)="data">
                  [{{ data.item.claim_number }}]
                  <!-- - {{ data.item.file_name }} -->
                </template>
                <!-- Column: payment_method_id -->
                <template #cell(image_url)="data">
                  <!-- <a target="_blank" :href="data.item.file_url">下</a> -->
                  <a class="color1" @click="previewImageSet(claims, data.index)"
                    >預覽</a
                  >
                </template>

                <template #cell(created_at)="data">
                  {{ getDateTime(data.item.created_at) }}
                </template>

                <template #cell(actions)="data">
                  <b-dropdown
                    class="action-trigger-btn"
                    :right="$store.state.appConfig.isRTL"
                  >
                    <template #button-content>
                      <feather-icon
                        icon="EyeIcon"
                        size="16"
                        class="align-middle text-body"
                      />
                    </template>

                    <b-dropdown-item
                      :to="{ name: 'worker-claim-list' }"
                      v-if="ability.can('read', 'user_claims')"
                    >
                      <feather-icon icon="EditIcon" />
                      <span class="align-middle ml-50">查看報銷資料</span>
                    </b-dropdown-item>

                    <b-dropdown-item
                      v-if="
                        ability.can('claims_document_delete', 'worker_detail')
                      "
                      @click="removeClaimDocument(data.item.id)"
                    >
                      <feather-icon icon="Trash2Icon" />
                      <span class="align-middle ml-50">刪除文件</span>
                    </b-dropdown-item>
                  </b-dropdown>
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BTable,
  BRow,
  BCol,
  BAlert,
  BLink,
  BButton,
  BDropdown,
  BDropdownItem,
  BCollapse,
  BBadge,
  VBToggle,
} from "bootstrap-vue";
import { ref, onUnmounted, onMounted } from "@vue/composition-api";
import vSelect from "vue-select";
import router from "@/router";
import store from "@/store";
import { formatCurrency } from "@core/utils/filter";
import axiosIns from "@/libs/axios";
import workerStoreModule from "../workerStoreModule";
import salaryStoreModule from "../salary/salaryStoreModule";
import ability from "@/libs/acl/ability";
import ModalSalaryRecord from "../modal/ModalSalaryRecord";
import ModalDocument from "../modal/ModalDocument";
import ModalWorkerExpenseDocuments from "../modal/ModalWorkerExpenseDocuments.vue";
import ModalPdf from "@/layouts/components/file/ModalPdf.vue";
import ModalImageViewer from "@/layouts/components/file/ModalImageViewer.vue";

export default {
  components: {
    BCard,
    BCardBody,
    BTable,
    BRow,
    BCol,
    BAlert,
    BLink,
    BButton,
    BDropdown,
    BDropdownItem,
    BCollapse,
    BBadge,
    vSelect,
    ModalSalaryRecord,
    ModalDocument,
    ModalWorkerExpenseDocuments,
    ModalPdf,
    ModalImageViewer,
  },
  data() {
    return {
      ability,
      detailKey: null,
      salaryRecordData: { id: 0 },
      documentData: {},
      pdfUrl: null,
      loadingPDF: false,
      title: null,
      previewImage: null,
      selected_file: null,
      userData: JSON.parse(localStorage.getItem("userData")),
      // modalUsers: [],
    };
  },
  directives: {
    "b-toggle": VBToggle,
  },
  methods: {
    hide() {
      this.$router.replace("/workers/list");
    },
    getFormatedDate(date) {
      const mm = date.getMonth() + 1;
      const dd = date.getDate();

      return [
        date.getFullYear(),
        (mm > 9 ? "" : "0") + mm,
        (dd > 9 ? "" : "0") + dd,
      ].join("-");
    },
    exportSalary(record) {
      if (!record) {
        console.error("找不到薪資記錄:");
        return;
      }

      // 簡化參數：直接使用 salary_summary_id
      const routeParams = {
        id: record.id, // 使用 salary_summary_id
      };

      // 簡化查詢參數：只需要 salary_summary_id
      const queryParams = {
        salary_summary_id: record.id, // 薪資匯總記錄 ID
        // 保留向後兼容參數（可選）
        // salary_type: record.salary_type,
        // reference_year: record.reference_year ,
        // reference_month: record.reference_month || new Date().getMonth() + 1
      };

      // 導航到預覽頁面
      this.$router
        .push({
          name: "worker-salary-preview",
          params: routeParams,
          query: queryParams
        })
        .catch((err) => {
          console.error("導航錯誤:", err);
        });

      console.log("導航到薪資預覽頁面，簡化參數:", { params: routeParams, query: queryParams });
    },
    handleModalSalaryRecord(item) {
      if (item) {
        this.salaryRecordData = item;
      } else {
        this.salaryRecordData = { id: 0 };
      }
      this.$bvModal.show("modal-salary-record");
    },
    removeDocument(id) {
      this.$swal({
        title: "確定?",
        text: "你的操作是最終的，你將無法恢復該文件。",
        showCancelButton: true,
        confirmButtonText: "刪除",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("worker/deleteWorkerDocument", { id })
            .then((response) => {
              this.$swal({
                text: response.data.message,
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.refetchData();
            })
            .catch((error) => {
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },
    removeClaimDocument(id) {
      this.$swal({
        title: "確定?",
        text: "你的操作是最終的，你將無法恢復該文件。",
        showCancelButton: true,
        confirmButtonText: "刪除",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("worker/deleteClaimDocument", { id })
            .then((response) => {
              this.$swal({
                text: response.data.message,
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.refetchData();
            })
            .catch((error) => {
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },
    deleteSalaryRecord(id) {
      if (this.workerData.salaryRecord.length <= 1) {
        this.makeToast(
          "danger",
          "Warning",
          "Please submit at least one salary record."
        );
        return;
      }

      this.$swal({
        title: "確定?",
        text: "你的操作是最終的，你將無法恢復該出糧薪金記錄。",
        showCancelButton: true,
        confirmButtonText: "刪除",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("worker/deleteSalaryRecord", { id: id })
            .then((response) => {
              this.$swal({
                text: response.data.message,
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.refetchData();
            })
            .catch((error) => {
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },
    previewImageSet(item, index) {
      console.log("item", item);
      if (item && item.length > 0) {
        this.previewImage = item.map((ele) => {
          return {
            id: ele.id,
            src: ele.file_url || ele.image_url,
            name: ele.file_name || ele.name,
            type:
              (ele.file_path || ele.image_path).indexOf("pdf") > -1
                ? "application/pdf"
                : "image/jpeg",
          };
        });
      } else {
        this.previewImage = [];
      }
      this.selected_file = index;

      setTimeout(() => {
        this.$bvModal.show(`modal-image-viewer`);
      });
    },
    handleModelDocument(item) {
      if (item) {
        this.documentData = item;
      } else {
        this.documentData = { id: 0 };
      }

      this.$bvModal.show("modal-document");
    },
    deleteWorker(id) {
      this.$swal({
        title: "你確定嗎?",
        text: "您的決定是最終決定，您將無法取回這記錄。",
        showCancelButton: true,
        confirmButtonText: "刪除",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("worker/deleteWorker", { id })
            .then((response) => {
              this.$swal({
                text: response.data.message,
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.refetchData();
            })
            .catch((error) => {
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },
    // showUsersModal(users) {
    //   this.modalUsers = users;
    //   this.isUsersModalVisible = true;
    // },
    makeToast(variant = null, title = null, content = null) {
      this.$bvToast.toast(content, {
        title: title,
        variant,
        solid: true,
      });
    },
    // 獲取項目狀態名稱
    getProjectStatusName(statusId) {
      if (!statusId || !this.projectStatusesOptions?.length) return "";

      const status = this.projectStatusesOptions.find((s) => s.id === statusId);
      return status?.name || "";
    },

    // 獲取經理名稱
    getManagerName(managerId) {
      if (!managerId || !this.managerOptions?.length) return "";

      const manager = this.managerOptions.find((m) => m.id === managerId);
      return manager?.name_cn || "";
    },
    getDailySalarySummary(salaryRecords) {
      if (!salaryRecords || salaryRecords.length <= 1) {
        return null;
      }

      return {
        totalOtAmount: salaryRecords.reduce(
          (sum, record) => sum + (Number(record.ot_amount) || 0),
          0
        ),
        totalCommission: salaryRecords.reduce(
          (sum, record) => sum + (Number(record.commission) || 0),
          0
        ),
        totalAllowance: salaryRecords.reduce(
          (sum, record) => sum + (Number(record.allowance) || 0),
          0
        ),
        // totalMpfEmployer: salaryRecords.reduce((sum, record) => sum + (Number(record.mpf_employer) || 0), 0),
        // totalMpfEmployee: salaryRecords.reduce((sum, record) => sum + (Number(record.mpf_employee) || 0), 0),
        totalSalary: salaryRecords.reduce(
          (sum, record) => sum + (Number(record.total_salary) || 0),
          0
        ),
      };
    },
    // 格式化貨幣
    formatCurrency(amount) {
      if (!amount) return "0.00";
      return Number(amount).toFixed(2);
    },

    // 獲取薪資類型名稱

    getSalaryType(salaryType) {
      const salaryTypeOptions = [
        { id: 1, name: "日薪" },
        { id: 2, name: "月薪" },
        { id: 3, name: "以工程項目計算" },
      ];
      return (
        salaryTypeOptions.find((s) => s.id === salaryType) || {
          name: "- -",
        }
      );
    },

    getSalaryReceiveMethod(id) {
      if (!id || !this.salaryReceiveMethodOptions?.length)
        return { name: "- -" };
      return (
        this.salaryReceiveMethodOptions.find((s) => s.id === id) || {
          name: "- -",
        }
      );
    },
    toggleRowDetails(key, row) {
      this.detailKey = key;
      row.toggleDetails();
    },
  },
  setup() {
    const workerData = ref({});

    const WORKER_APP_STORE_MODULE_NAME = "worker";
    const SALARY_APP_STORE_MODULE_NAME = "salary";

    if (!store.hasModule(WORKER_APP_STORE_MODULE_NAME))
      store.registerModule(WORKER_APP_STORE_MODULE_NAME, workerStoreModule);

    if (!store.hasModule(SALARY_APP_STORE_MODULE_NAME))
      store.registerModule(SALARY_APP_STORE_MODULE_NAME, salaryStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(WORKER_APP_STORE_MODULE_NAME))
        store.unregisterModule(WORKER_APP_STORE_MODULE_NAME);
      if (store.hasModule(SALARY_APP_STORE_MODULE_NAME))
        store.unregisterModule(SALARY_APP_STORE_MODULE_NAME);
    });

    const tableColumns = ref([
      // { key: "id", label: "編號", sortable: true },
      { key: "expense", stickyColumn: true, label: "薪金支出", formatter: formatCurrency },
      // { key: "invoice", label: "發票編號" },
      { key: "prepayment", label: "預支", formatter: formatCurrency },
      { key: "salary_type", label: "薪資形式", sortable: true },
      { key: "year_month", label: "出糧月份", sortable: true },
      { key: "export", label: "列印" },
      { key: "show_details", label: "詳情" },
      // { key: "working_days", label: "工作天數" },
      // { key: "day_salary", label: "每日工資", formatter: formatCurrency },
      { key: "gross_salary", label: "應發工資", sortable: true, formatter: formatCurrency },
      { key: "actual_salary", label: "實發工資", sortable: true, formatter: formatCurrency },
      { key: "salary_receive_method_id", label: "收款方式", sortable: true },
      // { key: "expense.check_number", label: "支票號碼", sortable: true },
      // { key: "working_date", label: "工作日期", sortable: true },
      { key: "remark", label: "備註", sortable: true },
      // { key: "is_approved", label: "已審核", sortable: true },
      // { key: "is_rejected", label: "已拒絕" },
      // { key: "admin_remark", label: "管理員備註" },
    ]);
    const projectTableColumns = ref([
      {
        key: "project_number",
        label: "編號",
        sortable: true,
        class: "sticky-column",
      },
      // { key: "project_name", label: "項目名稱", sortable: true },
      { key: "address", label: "工程地址", sortable: true },
      { key: "actions", label: "操作" },
      { key: "period", label: "預定工期", sortable: true },
      { key: "project_status_id", label: "項目狀態" },
      { key: "manager_id", label: "工程經理", sortable: true },
      { key: "is_completed", label: "是否完成", sortable: true },
      { key: "created_at", label: "創建日期", sortable: true },
    ]);

    const documentTableColumns = ref([
      {
        key: "name",
        stickyColumn: true,
        label: "檔案名稱",
        sortable: true,
      },
      { key: "image_url", label: "預覽", sortable: true },
      { key: "created_at", label: "上傳日期", sortable: true },
      { key: "actions", label: "操作" },
    ]);

    /*   const salaryRecords = ref([
      {
        id: 1,
        position: "水電師傅",
        address: "何文田 勝利道8號",
        join_dates: "01/04/2022",
        payment_method: { id: 1, name: "一月幾次" },
        salary: "$10000",
      },
    ]); */

    const salaryRecords = ref([]);

    const projectsList = ref([]);

    const documents = ref([
      /*     {
        id: 1,
        name: "身分證副本",
        type: "副本",
        file_type: "image/jpeg",
        image_path: "images/fake/onyx.jpeg",
        // file_type: "image/png",
        // image_path:"images/icon/excel.png"
      },
      {
        id: 2,
        name: "PDF",
        type: "副本",
        file_type: "application/pdf",
        image_path: null,
      }, */
    ]);

    const claims = ref([]);

    const fetchProjects = (_, callback) => {
      axiosIns.get("/projects/").then((response) => {
        const { projects } = response.data;
        const currentUserId = String(router.currentRoute.params.id);

        const filteredProjects = projects.filter((project) => {
          // 移除 is_active 條件，先確認用戶ID篩選是否正確
          return (
            (Array.isArray(project.user_ids) &&
              project.user_ids.some((id) => String(id) === currentUserId)) ||
            String(project.manager_id) === currentUserId
          );
        });

        callback(filteredProjects);
        projectsList.value = filteredProjects;
      });
    };

    const refetchData = () => {
      store
        .dispatch("worker/fetchWorker", {
          id: router.currentRoute.params.id,
        })
        .then((response) => {
          workerData.value = response.data.entity;
          documents.value = response.data.entity.documents;
          claims.value = response.data.entity.claim_documents;
          console.log(response.data.entity.documents);

          // 獲取薪資記錄數據 - 使用與 salariesList.vue 相同的 API
          fetchSalaryRecords();
        })
        .catch((error) => {
          console.log("error when fetching worker", error);
          if (error.response.status === 404) {
            workerData.value = undefined;
          }
        });
    };

    // 新增：獲取薪資記錄的方法
    const fetchSalaryRecords = () => {
      const userId = router.currentRoute.params.id;

      // 使用與 salariesList.vue 相同的 API 參數格式
      const apiParams = {
        user_id: userId,
        perPage: 10, // 限制顯示最近10筆記錄
        page: 1,
        sortBy: "id",
        sortDesc: true,
      };

      axiosIns
        .get("/salary_records/", { params: apiParams })
        .then((response) => {
          const { salary_records } = response.data;
          salaryRecords.value = salary_records;
          console.log("獲取薪資記錄成功:", salary_records);
        })
        .catch((error) => {
          console.log("獲取薪資記錄時出錯:", error);
          salaryRecords.value = [];
        });
    };

    const paymentMethodStatusOptions = ref([]);
    const salaryReceiveMethodOptions = ref([]);
    const userDocumentTypeOptions = ref([]);
    const projectStatusesOptions = ref([]);
    const quotationOptions = ref([]);
    const managerOptions = ref([]);
    const customerOptions = ref([]);

    const refetchOption = () => {
      store
        .dispatch("app/fetchOptionList", {
          payment_methods: true,
          user_document_types: true,
          project_statuses: true,
          min_quotations: true,
          workers: true,
          customers: true,
          receive_methods: true,
        })
        .then((response) => {
          paymentMethodStatusOptions.value = response.data.payment_methods;
          userDocumentTypeOptions.value = response.data.user_document_types;
          projectStatusesOptions.value = response.data.project_statuses;
          quotationOptions.value = response.data.quotations;
          managerOptions.value = response.data.workers;
          customerOptions.value = response.data.customers;
          salaryReceiveMethodOptions.value =
            response.data.receive_methods.sort ((a, b) => a.sort_order - b.sort_order);
        });
    };

    onMounted(() => {
      refetchData();
      refetchOption();
    });

    return {
      projectsList,
      fetchProjects,
      workerData,
      salaryRecords,
      tableColumns,
      documents,
      documentTableColumns,
      refetchData,
      paymentMethodStatusOptions,
      userDocumentTypeOptions,
      projectStatusesOptions,
      quotationOptions,
      salaryReceiveMethodOptions,
      managerOptions,
      customerOptions,
      projectTableColumns,
      claims,
    };
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@/assets/scss/variables/_variables.scss";
</style>
<style lang="scss" scoped>
.info-wrap {
  .info-label {
    display: inline-block;
    width: 100px;
  }
  .info-item {
    margin-bottom: 1rem;
    .icon {
      display: inline-block;
      margin-right: 1rem;
      width: 15px;
      svg {
        margin: auto;
        display: block;
      }
    }
  }
}
</style>